/**
 * Web Push Notifications utility functions
 * Minimal implementation for push notifications only (no offline/PWA features)
 */

export interface PushSubscriptionState {
  supported: boolean
  permission: NotificationPermission
  subscribed: boolean
  subscription: PushSubscription | null
}

/**
 * Check if push notifications are supported in this browser
 */
export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/**
 * Register the service worker for push notifications
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers are not supported in this browser')
    return null
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js')
    console.log('Service worker registered:', registration)
    return registration
  } catch (error) {
    console.error('Service worker registration failed:', error)
    return null
  }
}

/**
 * Request notification permission from the user
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    throw new Error('Notifications are not supported in this browser')
  }

  const permission = await Notification.requestPermission()
  return permission
}

/**
 * Subscribe to push notifications
 * @param vapidPublicKey - VAPID public key from backend
 * @returns PushSubscription object to send to backend
 */
export async function subscribeToPush(
  vapidPublicKey: string,
): Promise<PushSubscription> {
  const registration = await navigator.serviceWorker.ready

  if (!registration.pushManager) {
    throw new Error('Push manager is not available')
  }

  try {
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    })

    return subscription
  } catch (error) {
    console.error('Failed to subscribe to push notifications:', error)
    throw error
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  const registration = await navigator.serviceWorker.ready

  if (!registration.pushManager) {
    return false
  }

  const subscription = await registration.pushManager.getSubscription()

  if (subscription) {
    const success = await subscription.unsubscribe()
    return success
  }

  return false
}

/**
 * Get current push subscription
 */
export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) {
    return null
  }

  const registration = await navigator.serviceWorker.ready
  return await registration.pushManager.getSubscription()
}

/**
 * Get current push subscription state
 */
export async function getSubscriptionStatus(): Promise<PushSubscriptionState> {
  if (!isPushSupported()) {
    return {
      supported: false,
      permission: 'default',
      subscribed: false,
      subscription: null,
    }
  }

  const subscription = await getCurrentSubscription()

  return {
    supported: true,
    permission: Notification.permission,
    subscribed: subscription !== null,
    subscription,
  }
}

/**
 * Convert VAPID key from base64 URL-safe to Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): BufferSource {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }

  return outputArray
}
